import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import { ShoppingCartIcon, UserCircleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { buildSignUpUrl } from '../lib/cognitoUrls';
import { consumePendingSignupConfirmation } from '../lib/signupConfirmation';
import { clearGuestCheckoutSession } from '../services/checkout/guestCheckoutSession';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const auth = useAuth();
  const { user, checkAuth } = useAuthStore();
  const { getItemCount, fetchCart, transferAnonymousCart } = useCartStore();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const prevAuthRef = useRef<boolean>(false);

  const isAuthenticated = auth.isAuthenticated;
  const isLoading = auth.isLoading;

  useEffect(() => {
    if (isAuthenticated) {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const handleAuthChange = async () => {
      // Check if user just logged in (transition from false to true)
      const wasAuthenticated = prevAuthRef.current;
      prevAuthRef.current = isAuthenticated;

      if (isAuthenticated && !wasAuthenticated) {
        // A guest who started checkout (see CartPage.tsx) may have an
        // abandoned server-side guest cart at this point — its items were
        // already moved out of sessionStorage when guest checkout began, so
        // there is nothing left here to transfer onto the new session. The
        // logged-in user's own cart wins; the abandoned guest cart's hold
        // simply expires via its normal TTL. Clear the guest session so a
        // later logout doesn't resurrect a stale contact.
        clearGuestCheckoutSession();

        if (consumePendingSignupConfirmation()) {
          toast.success('Email confirmed. Welcome to Pay2Rent.me.');
        }

        // User just logged in - transfer anonymous cart
        try {
          const result = await transferAnonymousCart();
          if (result.transferred > 0) {
            toast.success(`${result.transferred} item(s) added to your cart!`);
          }
          if (result.failed > 0) {
            toast.error(`Failed to add ${result.failed} item(s) to cart`);
          }
        } catch (error) {
          console.error('Failed to transfer anonymous cart:', error);
        }
        
        // Fetch cart after transfer
        await fetchCart();
      } else if (isAuthenticated) {
        // Already authenticated, just fetch cart
        fetchCart();
      }
    };

    handleAuthChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSignIn = () => {
    auth.signinRedirect();
  };

  const handleSignUp = () => {
    window.location.href = buildSignUpUrl();
  };

  const handleSignOut = async () => {
    await auth.removeUser();
    localStorage.removeItem('accessToken');
    navigate('/');
  };

  const cartItemCount = getItemCount();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold">
                <span className="text-primary-600">Pay2Rent</span>
                <span className="text-yellow-500">.</span>
                <span className="text-primary-600">me</span>
              </span>
            </Link>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Desktop Navigation — cart stays outside so phones can reach it */}
              <nav className="hidden md:flex items-center space-x-4">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/my-rentals"
                      className="text-gray-700 hover:text-primary-600 transition-colors"
                    >
                      My Rentals
                    </Link>
                    {user?.isOwner && (
                      <Link
                        to="/my-listings"
                        className="text-gray-700 hover:text-primary-600 transition-colors"
                      >
                        My Listings
                      </Link>
                    )}
                    {user?.isAdmin && (
                      <Link
                        to="/admin"
                        className="text-gray-700 hover:text-primary-600 transition-colors"
                      >
                        Admin
                      </Link>
                    )}
                  </>
                ) : null}
              </nav>

              <Link
                to="/cart"
                className="relative text-gray-700 hover:text-primary-600 p-2"
                aria-label={cartItemCount > 0 ? `Cart, ${cartItemCount} items` : 'Cart'}
              >
                <ShoppingCartIcon className="h-6 w-6" />
                {cartItemCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Link>

              {/* Desktop account actions */}
              <div className="hidden md:flex items-center space-x-4">
                {isAuthenticated ? (
                  <div className="relative group">
                    <button className="flex items-center text-gray-700 hover:text-primary-600 py-2">
                      <UserCircleIcon className="h-6 w-6" />
                      <span className="ml-2 hidden sm:block">{user?.displayName}</span>
                    </button>
                    <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                      <div className="w-48 bg-white rounded-lg shadow-lg py-2">
                        {!user?.isOwner && (
                          <Link
                            to="/become-owner"
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Become an Owner
                          </Link>
                        )}
                        <button
                          onClick={handleSignOut}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleSignIn}
                      className="text-gray-700 hover:text-primary-600 transition-colors"
                    >
                      Log In
                    </button>
                    <button
                      onClick={handleSignUp}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Sign Up
                    </button>
                  </>
                )}
              </div>

              {/* Mobile Hamburger Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-gray-700 hover:text-primary-600 p-2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Drawer Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex justify-between items-center p-4 border-b">
            <span className="text-xl font-bold">
              <span className="text-primary-600">Menu</span>
            </span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-gray-700 hover:text-primary-600 p-2"
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <nav className="flex-1 overflow-y-auto p-4">
            {isAuthenticated ? (
              <div className="space-y-4">
                {/* User Info */}
                <div className="pb-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                    <span className="font-medium text-gray-900">{user?.displayName}</span>
                  </div>
                </div>

                {/* Navigation Links */}
                <Link
                  to="/my-rentals"
                  className="block py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Rentals
                </Link>

                {user?.isOwner && (
                  <Link
                    to="/my-listings"
                    className="block py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Listings
                  </Link>
                )}

                {user?.isAdmin && (
                  <Link
                    to="/admin"
                    className="block py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                )}

                {!user?.isOwner && (
                  <Link
                    to="/become-owner"
                    className="block py-3 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg px-3 transition-colors font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Become an Owner
                  </Link>
                )}

                <Link
                  to="/cart"
                  className="flex items-center py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <ShoppingCartIcon className="h-5 w-5 mr-3" />
                  <span>Cart</span>
                  {cartItemCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Link>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Link
                  to="/become-owner"
                  className="block py-3 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg px-3 transition-colors font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Become an Owner
                </Link>
                <Link
                  to="/cart"
                  className="flex items-center py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <ShoppingCartIcon className="h-5 w-5 mr-3" />
                  <span>Cart</span>
                  {cartItemCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Link>
                <button
                  onClick={() => {
                    handleSignIn();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg px-3 transition-colors"
                >
                  Log In
                </button>
                <button
                  onClick={() => {
                    handleSignUp();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors text-center"
                >
                  Sign Up
                </button>
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-600 text-sm mb-4 md:mb-0">
              © 2025 Pay2Rent.me. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              {!user?.isOwner && (
                <Link to="/become-owner" className="text-gray-600 hover:text-primary-600">
                  Become an Owner
                </Link>
              )}
              <Link to="/connect" className="text-gray-600 hover:text-primary-600">
                Connect your AI
              </Link>
              <Link to="/privacy" className="text-gray-600 hover:text-primary-600">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-600 hover:text-primary-600">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;

